import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CreditCard, Wallet, FileText, Image as ImageIcon } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Input, Label, Textarea, FieldHint, FieldError } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { useUploadInvoice } from '@/lib/api/hooks'
import { useToast } from '@/components/ui/ToastProvider'
import { cn } from '@/lib/utils'
import type { PaidBy } from '@/lib/api/types'

export function UploadDialog({
  open,
  onClose,
  companyId,
}: {
  open: boolean
  onClose: () => void
  companyId: string
}) {
  const upload = useUploadInvoice()
  const toast = useToast()
  const nav = useNavigate()

  const [file, setFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [currency, setCurrency] = useState<'HUF' | 'EUR' | 'USD'>('HUF')
  const [paidBy, setPaidBy] = useState<NonNullable<PaidBy>>('company')
  const [category, setCategory] = useState('')
  const [projectCode, setProjectCode] = useState('')
  const [notes, setNotes] = useState('')

  // Preview URL — object URL takarítás unmount-kor és file váltáskor
  useEffect(() => {
    if (!file) {
      setPreviewUrl(null)
      return
    }
    const url = URL.createObjectURL(file)
    setPreviewUrl(url)
    return () => {
      URL.revokeObjectURL(url)
    }
  }, [file])

  function reset() {
    setFile(null)
    setCurrency('HUF')
    setPaidBy('company')
    setCategory('')
    setProjectCode('')
    setNotes('')
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!file) return
    try {
      const result = await upload.mutateAsync({
        companyId,
        file,
        currency,
        paidBy,
        category: category || undefined,
        projectCode: projectCode || undefined,
        notes: notes || undefined,
      })
      toast.show({
        tone: 'success',
        title: 'Feltöltve',
        description: `${file.name} · ${currency} · ${paidBy === 'personal' ? 'saját kártya' : 'céges'}`,
      })
      reset()
      onClose()
      // Azonnal nyissuk meg a részleteket — ott látszik a PDF preview, kategória, jóváhagyás stb.
      if (result?.invoiceId) {
        nav(`/invoices/${result.invoiceId}`)
      }
    } catch (err) {
      toast.show({
        tone: 'error',
        title: 'Hiba',
        description: err instanceof Error ? err.message : 'Ismeretlen hiba',
      })
    }
  }

  const isPdf = file?.type === 'application/pdf' || file?.name.toLowerCase().endsWith('.pdf')
  const isImage = file?.type.startsWith('image/')

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Számla feltöltése"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Mégse
          </Button>
          <Button
            onClick={(e) => onSubmit(e as unknown as React.FormEvent)}
            loading={upload.isPending}
            disabled={!file}
          >
            Feltöltés
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <Label htmlFor="file">Fájl (PDF / JPEG / PNG; max 25 MB)</Label>
          <input
            id="file"
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="block w-full text-sm text-slate-700 dark:text-slate-200 file:mr-3 file:rounded-md file:border-0 file:bg-brand-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-brand-700 hover:file:bg-brand-100 dark:file:bg-brand-900/30 dark:file:text-brand-200"
          />
          <FieldHint>A feltöltés után automatikusan a számla részleteire kerülsz.</FieldHint>
        </div>

        {/* Előnézet még submit ELŐTT */}
        {file && previewUrl && (
          <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-2">
            <div className="flex items-center gap-2 mb-2 text-xs text-slate-500 dark:text-slate-400">
              {isPdf ? <FileText className="h-3.5 w-3.5" /> : <ImageIcon className="h-3.5 w-3.5" />}
              <span className="truncate flex-1">{file.name}</span>
              <span>{(file.size / 1024).toFixed(1)} KB</span>
            </div>
            {isPdf ? (
              <iframe
                src={previewUrl}
                title="PDF előnézet"
                className="w-full h-64 rounded border border-slate-200 dark:border-slate-700 bg-white"
              />
            ) : isImage ? (
              <img
                src={previewUrl}
                alt="Számla előnézet"
                className="max-h-64 mx-auto rounded border border-slate-200 dark:border-slate-700 bg-white"
              />
            ) : (
              <div className="text-xs text-slate-500 p-3">Az előnézet csak PDF / kép formátumra elérhető.</div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="ud-currency">Deviza</Label>
            <Select
              id="ud-currency"
              value={currency}
              onChange={(e) => setCurrency(e.target.value as typeof currency)}
            >
              <option value="HUF">HUF (Magyar forint)</option>
              <option value="EUR">EUR (Euró)</option>
              <option value="USD">USD (US dollár)</option>
            </Select>
            <FieldHint>A számlán szereplő pénznem.</FieldHint>
          </div>
          <div>
            <Label>Ki fizette ki?</Label>
            <div className="grid grid-cols-2 gap-2 mt-0.5">
              <PaidByButton
                active={paidBy === 'company'}
                onClick={() => setPaidBy('company')}
                icon={<CreditCard className="h-4 w-4" />}
                label="Céges"
                hint="Cégtől kifizetve"
              />
              <PaidByButton
                active={paidBy === 'personal'}
                onClick={() => setPaidBy('personal')}
                icon={<Wallet className="h-4 w-4" />}
                label="Saját"
                hint="Saját kártya / zsebből"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="category">Költségkategória</Label>
            <Input
              id="category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="pl. iroda, marketing"
              maxLength={80}
            />
          </div>
          <div>
            <Label htmlFor="projectCode">Projekt / költséghely</Label>
            <Input
              id="projectCode"
              value={projectCode}
              onChange={(e) => setProjectCode(e.target.value)}
              placeholder="pl. CORE"
              maxLength={80}
            />
          </div>
        </div>

        <div>
          <Label htmlFor="notes">Megjegyzés</Label>
          <Textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Opcionális belső jegyzet"
            rows={3}
          />
        </div>

        {upload.isError && <FieldError>{(upload.error as Error).message}</FieldError>}
      </form>
    </Modal>
  )
}

function PaidByButton({
  active,
  onClick,
  icon,
  label,
  hint,
}: {
  active: boolean
  onClick: () => void
  icon: React.ReactNode
  label: string
  hint: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex flex-col items-start gap-0.5 rounded-md border px-3 py-2 text-sm text-left transition-colors',
        active
          ? 'border-brand-500 bg-brand-50 text-brand-800 dark:border-brand-500 dark:bg-brand-900/30 dark:text-brand-100'
          : 'border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800',
      )}
      aria-pressed={active}
    >
      <span className="inline-flex items-center gap-1.5 font-medium">
        {icon}
        {label}
      </span>
      <span className="text-[11px] text-slate-500 dark:text-slate-400">{hint}</span>
    </button>
  )
}
